import React, { useState, useRef, useEffect, useMemo } from "react";
import MonacoEditor from "react-monaco-editor";
import { Box, Snackbar, Alert, Dialog, TextField, Button, Tooltip } from "@mui/material";
import * as monaco from 'monaco-editor';
import { useVariableTooltip } from "hooks/useVariableTooltip";
import { DialogVariableTooltip } from "components/Common/DialogVariableTooltip";
import { createVariable } from "../../redux-store/variableReducers/variableActions";
import { useDispatch, connect } from "react-redux";
import { useSnackbar } from "../../contexts/CustomSnackbarContext";
import { getVariableSet } from "redux-store/variableSetReducers/variableSetActions";
import { useTheme } from "contexts/ThemeContext";
import { setIsSaveRequest } from "redux-store/variableSetReducers/variableSetSlice";

// Define custom theme for the editor
monaco.editor.defineTheme("custom-monaco-theme", {
  base: 'vs',
  inherit: true,
  rules: [
    { token: "key", foreground: "A31515" },
    { token: "value", foreground: "0451A5" },
    { token: "number", foreground: "098658" },
    { token: "boolean", foreground: "FF8C00" },
  ],
  colors: {
    'editor.background': '#ffffff',             // Light mode background
    'editor.foreground': '#282a2c',             // Light mode text
    'editorLineNumber.foreground': '#64748B',   // Light mode line numbers
    'editor.lineHighlightBackground': '#f5f5f5', // Light gray for line highlight
    'editor.lineHighlightBorder': '#e5e5e5',    // Lighter gray for line highlight border
    'editorGutter.background': '#e9e9e9',       // Light mode gutter
    'editor.selectionBackground': '#add6ff',    // Light blue for selection
    'editor.inactiveSelectionBackground': '#e5ebf1', // Light gray for inactive selection
  }
});

// Define dark theme for the editor
monaco.editor.defineTheme("custom-monaco-theme-dark", {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: "key", foreground: "9CDCFE" },
    { token: "value", foreground: "CE9178" },
    { token: "number", foreground: "B5CEA8" },
    { token: "boolean", foreground: "569CD6" },
  ],
  colors: {
    'editor.background': '#0e1525',             // Dark theme background
    'editor.foreground': '#d4d4d4',             // VS Code light gray text
    'editorLineNumber.foreground': '#94a3b8',   // Dark theme line numbers
    'editor.lineHighlightBackground': '#1a2234', // Darker blue for line highlight
    'editor.lineHighlightBorder': '#2a2d2e',    // Dark gray for line highlight border
    'editorGutter.background': '#0e1525',       // Dark theme gutter
    'editor.selectionBackground': '#264f78',     // VS Code selection
    'editor.inactiveSelectionBackground': '#3a3d41', // VS Code inactive selection
    'editor.wordHighlightBackground': '#575757', // VS Code word highlight
    'editor.findMatchBackground': '#515c6a',     // VS Code find match
    'editor.findMatchHighlightBackground': '#515c6a', // VS Code find match highlight
  }
});

monaco.languages.register({ id: "jsonWithCustomColors" });

monaco.languages.setMonarchTokensProvider("jsonWithCustomColors", {
  tokenizer: {
    root: [
      [/\"([a-zA-Z0-9_]+)\"\s*:/, { token: "key" }],
      [/\"([^\"\\]*(?:\\.[^\"\\]*)*)\"/, { token: "value" }],
      [/(\d+)/, { token: "number" }],
      [/\b(true|false|null)\b/, { token: "boolean" }],
      [/[{}[\]]/, { token: "delimiter" }],
      [/,/, { token: "delimiter" }],
    ],
  },
});

const BodyRaw = ({ body, onBodyChange, environmentVariables, collectionVariables, folderVariables, selectedEnvironmentId, selectedEnvironmentName, apiResponseVariables }) => {
  const dispatch = useDispatch();
  const { openSnackbar } = useSnackbar();
  const [bodyRaw, setBodyRaw] = useState(JSON.stringify(body, undefined, 4));
  const [error, setError] = useState(null);
  const editorRef = useRef(null);
  const monacoInstance = useRef(null);
  const editorInstance = useRef(null);
  const [selectedText, setSelectedText] = useState("");
  const [newVariableName, setNewVariableName] = useState("");
  const [addVariableDialogOpen, setAddVariableDialogOpen] = useState(false);
  const [latestEnvironmentVariables, setLatestEnvironmentVariables] = useState({});
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({ left: 0, top: 0 });
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const tooltipTimeoutRef = useRef(null);

  const { isDarkMode } = useTheme();

  const variableSources = useMemo(() => {
    return {
      environment: latestEnvironmentVariables || {},
      collection: collectionVariables || {},
      folder: folderVariables || {},
      apiResponseVariables: apiResponseVariables || {}
    };
  }, [latestEnvironmentVariables, collectionVariables, folderVariables, apiResponseVariables]);

  useEffect(() => {
    setLatestEnvironmentVariables(environmentVariables);
  }, [environmentVariables]);

  const {
    handleMouseMove,
    selectedVariable,
    handleClose,
    setVariableKey,
    setVariableValue,
    variableKey,
    variableValue,
    isResolvingFunction = false
  } = useVariableTooltip(
    editorRef,
    variableSources
  );

  const fetchSelectedEnvironment = async () => {
    if (selectedEnvironmentId) {
      const result = await dispatch(getVariableSet({
        variableSetId: selectedEnvironmentId
      })).unwrap();
    }
  };

  const handleOpenAddVariableDialog = () => {
    setAddVariableDialogOpen(true);
  };

  const handleSaveNewVariable = async () => {
    if (!newVariableName.trim()) return;
    const selection = editorInstance.current?.getSelection();
    const model = editorInstance.current?.getModel();
    const data = {
      resourceId: selectedEnvironmentId,
      resourceType: "VariableSet",
      var_name: newVariableName,
      var_value: selectedText
    };

    try {
      const newVariableResponse = await dispatch(createVariable(data));
      if (newVariableResponse.payload?.errors?.length > 0) {
        openSnackbar({ message: newVariableResponse.payload.errors.join(", "), severity: 'error' });
        return;
      } else if (newVariableResponse.payload?.variable) {
        openSnackbar({ message: "Variable created successfully", severity: 'success' });
      } else {
        openSnackbar({ message: "Something Went Wrong", severity: 'error' });
        return;
      }

      if (selection && model) {
        const range = new monaco.Range(
          selection.startLineNumber,
          selection.startColumn,
          selection.endLineNumber,
          selection.endColumn
        );

        model.pushEditOperations(
          [],
          [{ range, text: `{{${newVariableName}}}` }]
        );

        // Get the updated content and update parent
        try {
          const updatedContent = model.getValue();
          const jsonObject = JSON.parse(updatedContent);
          onBodyChange(jsonObject);

          // Now trigger the save request after body is updated
          setTimeout(() => {
            dispatch(setIsSaveRequest(true));
          }, 100);
        } catch (err) {
          console.error("Error parsing JSON:", err);
          openSnackbar({ message: "Invalid JSON format after variable insertion.", severity: 'error' });
          return;
        }
      }

      if (newVariableResponse.payload.variable) {
        setLatestEnvironmentVariables(prev => {
          const updated = {
            ...prev,
            [newVariableName]: selectedText
          };
          return updated;
        });
        await fetchSelectedEnvironment();
      }

      setAddVariableDialogOpen(false);
      setNewVariableName("");
    } catch (error) {
      openSnackbar({ message: error, severity: 'error' });
      console.error("Error saving variable:", error);
    }
  };

  const editorDidMount = (editor, monaco) => {
    monacoInstance.current = monaco;
    editorInstance.current = editor;

    // Force theme application
    monaco.editor.setTheme(isDarkMode ? "custom-monaco-theme-dark" : "custom-monaco-theme");

    editor.addAction({
      id: "customInsertVariable", // Unique identifier for your custom action
      label: "Add Variable", // Label that will appear in the context menu
      contextMenuGroupId: "navigation", // Optional: Group in the context menu (e.g., 'navigation' or 'edit')
      contextMenuOrder: 1, // Optional: Specify the order of this action
      run: (ed) => {
        const selection = ed.getSelection();
        if (!selection) return;

        const model = ed.getModel();
        const selectedText = model?.getValueInRange(selection);

        if (selectedText) {
          setSelectedText(selectedText);
          handleOpenAddVariableDialog()
        }
      },
    });

    editor.onMouseMove((e) => {
      if (!e.target || !e.target.position) return;
      const model = editor.getModel();
      if (!model) return;

      const position = e.target.position;
      const lineContent = model.getLineContent(position.lineNumber);
      const regex = /{{(.*?)}}[.,;:)?]?/g;
      let match;
      let isOverVariable = false;

      while ((match = regex.exec(lineContent)) !== null) {
        if (
          position.column >= match.index + 1 &&
          position.column <= match.index + match[0].length
        ) {
          isOverVariable = true;
          const { clientX, clientY } = e.event.browserEvent;
          // Get the editor's coordinates
          const editorCoords = editor.getDomNode().getBoundingClientRect();
          // Calculate position for bottom placement
          const lineHeight = editor.getOption(monaco.editor.EditorOption.lineHeight);
          const coords = editor.getScrolledVisiblePosition(position);
          setTooltipStyle({
            left: editorCoords.left + coords.left,
            top: editorCoords.top + coords.top + lineHeight + 5,
          });
          setTooltipVisible(true);
          handleMouseMove({
            clientX,
            clientY,
            variableName: match[1].trim(),
            event: e,
          });
          return;
        }
      }

      if (!isOverVariable) {
        if (!isHoveringTooltip) {
          // Clear any existing timeout
          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
          }
          // Set a timeout to hide the tooltip after a short delay
          tooltipTimeoutRef.current = setTimeout(() => {
            if (!isHoveringTooltip) {
              setTooltipVisible(false);
              handleClose();
            }
          }, 300);
        }
      }
    });

    editor.onMouseLeave(() => {
      if (!isHoveringTooltip) {
        // Clear any existing timeout
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }
        // Set a timeout to hide the tooltip after a short delay
        tooltipTimeoutRef.current = setTimeout(() => {
          if (!isHoveringTooltip) {
            setTooltipVisible(false);
            handleClose();
          }
        }, 300);
      }
    });
  };

  useEffect(() => {
    setBodyRaw(JSON.stringify(body, undefined, 4));
  }, [body]);

  const handleOutsideClick = (event) => {
    if (editorRef.current && !editorRef.current.contains(event.target)) {
      try {
        const jsonObject = JSON.parse(bodyRaw.replace(/,\s*([}\]])/g, '$1'));
        onBodyChange(jsonObject);
      } catch (err) {
        openSnackbar({ message: "Invalid JSON format. Please correct it before saving.", severity: 'error' });
      }
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [bodyRaw]);

  return (
    <Box
      ref={editorRef}
      sx={{
        height: "auto",
        overflow: "auto",
        border: "1px solid var(--borderColor)",
        margin: "10px 0",
        borderRadius: "5px",
        position: "relative",
        backgroundColor: "var(--background-primary)",
      }}
      autoComplete="off"
    >
      <MonacoEditor
        height="35vh"
        language="jsonWithCustomColors"
        theme={isDarkMode ? "custom-monaco-theme-dark" : "custom-monaco-theme"}
        value={bodyRaw}
        onChange={(newBodyRaw) => {
          setBodyRaw(newBodyRaw);
          setError(null);
        }}
        editorDidMount={editorDidMount}
        options={{
          automaticLayout: false,
          wordWrap: "on",
          minimap: { enabled: false },
          lineNumbers: "on",
          tabSize: 8,
          fontSize: 11,
          scrollBeyondLastLine: false,
          scrollbar: {
            vertical: 'visible',
            horizontal: 'visible',
            verticalScrollbarSize: 14,
            horizontalScrollbarSize: 14,
            verticalSliderSize: 14,
            horizontalSliderSize: 14,
          },
          overviewRulerBorder: false,
          hideCursorInOverviewRuler: true,
          renderLineHighlight: 'all',
        }}
        autoComplete="off"
      />
      {(tooltipVisible || isHoveringTooltip) && (
        <div
          style={{
            position: 'fixed',
            left: tooltipStyle.left,
            top: tooltipStyle.top,
            zIndex: 9999,
          }}
          onMouseEnter={() => setIsHoveringTooltip(true)}
          onMouseLeave={() => {
            setIsHoveringTooltip(false);
            setTooltipVisible(false);
            handleClose();
          }}
        >
          <Tooltip
            open={true}
            placement="bottom"
            PopperProps={{
              sx: {
                '& .MuiTooltip-tooltip': {
                  backgroundColor: 'var(--background-primary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--borderColor)',
                  borderRadius: '6px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 8px 30px rgba(0, 0, 0, 0.12)',
                  padding: '8px',
                  minWidth: '200px',
                  marginTop: '-5px',
                },
                '& .MuiTooltip-arrow': {
                  color: "var(--text-primary)",
                  '&::before': {
                    border: '1px solid var(--borderColor)',
                    backgroundColor: 'var(--background-primary)',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                  },
                },
              },
            }}
            arrow
          >
            <div>
              <DialogVariableTooltip
                selectedVariable={selectedVariable || {}}
                environmentName={selectedEnvironmentName}
                setVariableKey={setVariableKey}
                setVariableValue={setVariableValue}
                variableKey={variableKey || ''}
                variableValue={variableValue || ''}
                isResolvingFunction={isResolvingFunction}
              />
            </div>
          </Tooltip>
        </div>
      )}

      <Dialog open={addVariableDialogOpen} onClose={() => setAddVariableDialogOpen(false)}>
        <Box className="p-4">
          <div>
            <span className="!text-lg">Enter Variable Name</span>
          </div>
          <TextField
            label="Enter Key"
            value={newVariableName}
            onChange={(e) => setNewVariableName(e.target.value)}
            placeholder="Variable Name"
            fullWidth
            className="my-2"
          />
          <TextField
            label="Selected Value"
            value={selectedText}
            placeholder="Variable Name"
            fullWidth
            disabled
            className="my-2"
          />
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSaveNewVariable}
          >
            Add Variable
          </Button>
        </Box>
      </Dialog>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    environmentVariables: state.variableSet.selectedEnvironmentVariablesJSON,
    collectionVariables: state.variableSet.selectedCollectionVariablesJSON,
    folderVariables: state.variableSet.selectedFolderVariablesJSON,
    apiResponseVariables: state.responseVariables.responseVariables,
    selectedEnvironmentId: state.variableSet.selectedEnvironmentVariables?.id ?? "",
    selectedEnvironmentName: state.variableSet.selectedEnvironmentVariables?.title,
  }
}
export default connect(mapStateToProps)(BodyRaw);
